<template>
  <div id="paper-trading-settings">
    <div class="container-fluid pt-4 px-4">
      <v-alert v-if="saveMessage" :type="saveError ? 'error' : 'success'" variant="tonal" density="comfortable" class="mb-4" rounded="lg">
        {{ saveMessage }}
      </v-alert>

      <v-alert
        v-for="(warning, idx) in sizingWarnings" :key="idx"
        type="warning" variant="tonal" density="comfortable" class="mb-4" rounded="lg" closable
      >
        {{ warning }}
      </v-alert>

      <v-card flat rounded="lg" class="gap-backtest-card mb-4" :class="{ 'order-mode-live': form.order_mode === 'live' }">
        <v-card-title class="px-5 py-4 d-flex align-center justify-space-between">
          <span class="text-h6 font-weight-medium">Order Mode</span>
          <v-chip :color="form.order_mode === 'live' ? 'error' : 'success'" variant="flat" size="small">
            {{ form.order_mode === 'live' ? 'LIVE — real broker orders' : 'Paper (simulated)' }}
          </v-chip>
        </v-card-title>
        <v-card-text class="px-5 pb-5">
          <v-alert type="warning" variant="tonal" density="comfortable" class="mb-4" rounded="lg">
            Paper mode only ever writes simulated rows. Live mode places real buy/sell orders with real money
            through AngelOne for every entry, partial booking, and exit. Switching back to Paper takes effect on the
            very next tick, but does <strong>not</strong> close any position already open at the broker — that's a
            manual action.
          </v-alert>
          <v-row align="center">
            <v-col cols="12" sm="6" md="4">
              <v-select
                v-model="orderModeSelection"
                :items="[{ title: 'Paper (simulated)', value: 'paper' }, { title: 'Live (real orders)', value: 'live' }]"
                label="Mode"
                variant="outlined"
                density="comfortable"
                hide-details
              />
            </v-col>
            <v-col v-if="orderModeSelection === 'live' && form.order_mode !== 'live'" cols="12" sm="6" md="5">
              <v-text-field
                v-model="orderModeConfirmText"
                label="Type LIVE to confirm"
                variant="outlined"
                density="comfortable"
                hide-details
              />
            </v-col>
            <v-col v-if="orderModeSelection === 'live' && form.order_mode !== 'live'" cols="12" sm="12" md="3">
              <v-btn
                color="error"
                variant="flat"
                rounded="lg"
                :loading="orderModeSaving"
                :disabled="orderModeConfirmText !== 'LIVE'"
                @click="confirmLiveMode"
              >
                Enable Live Orders
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-card flat rounded="lg" class="gap-backtest-card mb-4">
        <v-card-title class="px-5 py-4">
          <span class="text-h6 font-weight-medium">Capital &amp; Risk</span>
        </v-card-title>
        <v-card-text class="px-5 pb-5">
          <v-row>
            <v-col cols="12" sm="6" md="3">
              <v-text-field v-model.number="form.total_capital_rs" type="number" label="Total capital (₹)" variant="outlined" density="comfortable" />
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-text-field v-model.number="form.risk_per_trade_rs" type="number" label="Risk per trade (₹)" variant="outlined" density="comfortable" />
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-text-field v-model.number="form.max_exposure_rs" type="number" label="Max exposure per trade (₹)" variant="outlined" density="comfortable" />
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-text-field v-model.number="form.stop_buffer_pct" type="number" step="0.01" label="Stop-loss buffer (%)" variant="outlined" density="comfortable" />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model.number="form.stop_suggestion_percentile" type="number" step="1"
                label="Stop suggestion percentile" variant="outlined" density="comfortable"
                hint="Which percentile of a stock's historical adverse-move-against-the-position distribution the Watchlist page's Suggested Stop % uses (see the Watchlist page). Only affects future suggestions -- never rewrites an override you've already applied."
                persistent-hint
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-card flat rounded="lg" class="gap-backtest-card mb-4">
        <v-card-title class="px-5 py-4">
          <span class="text-h6 font-weight-medium">Order System Robustness</span>
        </v-card-title>
        <v-card-text class="px-5 pb-5">
          <v-row>
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model.number="form.max_slippage_pct" type="number" step="0.1" label="Slippage alert threshold (%)"
                variant="outlined" density="comfortable"
                hint="WhatsApp alert (not a block) when a live fill differs from the expected price by more than this"
                persistent-hint
              />
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model.number="form.consecutive_failure_limit" type="number" label="Consecutive failure limit"
                variant="outlined" density="comfortable"
                hint="Auto-switch to paper mode after this many live order failures in a row"
                persistent-hint
              />
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model.number="form.max_gap_pct" type="number" step="0.1" label="Max plausible gap (%)"
                variant="outlined" density="comfortable"
                hint="Gaps bigger than this are treated as bad data and skipped, not traded"
                persistent-hint
              />
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model.number="form.max_open_price_deviation_pct" type="number" step="0.1" label="Max open-vs-live-price deviation (%)"
                variant="outlined" density="comfortable"
                hint="Near market open, if today's open and the live price disagree by more than this, treated as a bad print"
                persistent-hint
              />
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model.number="form.quote_sanity_fail_limit" type="number" label="Quote sanity fail limit"
                variant="outlined" density="comfortable"
                hint="Retries silently this many ticks before giving up on the stock for the day and alerting"
                persistent-hint
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-card flat rounded="lg" class="gap-backtest-card mb-4">
        <v-card-title class="px-5 py-4 d-flex align-center justify-space-between">
          <span class="text-h6 font-weight-medium">Daily Loss Limit</span>
          <v-switch v-model="form.daily_loss_limit_enabled" color="primary" density="compact" hide-details />
        </v-card-title>
        <v-card-text class="px-5 pb-5">
          <v-row>
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                v-model.number="form.max_daily_loss_rs" type="number" label="Max daily loss (₹)"
                variant="outlined" density="comfortable" :disabled="!form.daily_loss_limit_enabled"
                hint="No new trades open once today's realized P&L drops to this. Open positions still exit normally."
                persistent-hint
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-card flat rounded="lg" class="gap-backtest-card mb-4">
        <v-card-title class="px-5 py-4">
          <span class="text-h6 font-weight-medium">Portfolio Risk Manager</span>
        </v-card-title>
        <v-card-text class="px-5 pb-5">
          <v-alert type="info" variant="tonal" density="comfortable" class="mb-4" rounded="lg">
            Caps concentration on top of the existing per-trade exposure budget above. All three are unlimited by
            default — leave a field blank to keep it that way, rather than entering 0 (which would block every
            trade). A rejected candidate still gets a shadow (what-if) simulation, same as ranking below the daily
            trade-slot limit does.
          </v-alert>
          <v-row>
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                v-model.number="form.max_positions_per_sector" type="number" label="Max open positions per sector"
                variant="outlined" density="comfortable" clearable
                hint="e.g. 1 = never hold two open positions in the same sector at once"
                persistent-hint
              />
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                v-model.number="form.max_sector_exposure_pct" type="number" step="1" label="Max sector exposure (% of budget)"
                variant="outlined" density="comfortable" clearable
                hint="% of the effective exposure budget one sector can use across all its open positions"
                persistent-hint
              />
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                v-model.number="form.max_concurrent_positions" type="number" label="Max concurrent open positions"
                variant="outlined" density="comfortable" clearable
                hint="Distinct from Max trades/day below -- this caps how many can be open at the same time"
                persistent-hint
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-card flat rounded="lg" class="gap-backtest-card mb-4">
        <v-card-title class="px-5 py-4 d-flex align-center justify-space-between">
          <span class="text-h6 font-weight-medium">Partial Profit Booking</span>
          <v-switch v-model="form.partial_booking_enabled" color="primary" density="compact" hide-details />
        </v-card-title>
        <v-card-text class="px-5 pb-5">
          <v-row>
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                v-model.number="form.partial_trigger_pct"
                type="number"
                label="Trigger: % of the way to target"
                variant="outlined"
                density="comfortable"
                :disabled="!form.partial_booking_enabled"
                hint="e.g. 50 = book once price is halfway from entry to target"
                persistent-hint
              />
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                v-model.number="form.partial_qty_pct"
                type="number"
                label="Quantity to book (%)"
                variant="outlined"
                density="comfortable"
                :disabled="!form.partial_booking_enabled"
                hint="e.g. 50 = sell half the position at the trigger"
                persistent-hint
              />
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-select
                v-model="form.partial_sl_action"
                :items="[{ title: 'Move to breakeven (entry price)', value: 'breakeven' }, { title: 'Keep original stop', value: 'unchanged' }]"
                label="Stop-loss on remaining qty after booking"
                variant="outlined"
                density="comfortable"
                :disabled="!form.partial_booking_enabled"
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-card flat rounded="lg" class="gap-backtest-card mb-4">
        <v-card-title class="px-5 py-4">
          <span class="text-h6 font-weight-medium">Signal Ranking</span>
        </v-card-title>
        <v-card-text class="px-5 pb-5">
          <v-row>
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                v-model.number="form.max_trades_per_day"
                type="number"
                label="Max trades per day"
                variant="outlined"
                density="comfortable"
                hint="Only the top-ranked signals are opened once this many are already open today"
                persistent-hint
              />
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                v-model.number="form.leverage_multiplier"
                type="number"
                step="0.5"
                label="Leverage multiplier"
                variant="outlined"
                density="comfortable"
                hint="Effective exposure cap = total capital × this, capped by Max exposure per trade"
                persistent-hint
              />
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                v-model.number="form.min_win_rate_threshold_pct"
                type="number"
                label="Min. historical win rate (%)"
                variant="outlined"
                density="comfortable"
                hint="A stock's statistically-adjusted win rate must clear this to be eligible at all"
                persistent-hint
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" sm="6" md="4">
              <v-select
                v-model="form.position_sizing_mode"
                :items="[
                  { title: 'Shared pool (first-ranked claims first)', value: 'shared_pool' },
                  { title: 'Equal split across daily slots', value: 'equal_split' },
                ]"
                label="Position sizing mode"
                variant="outlined"
                density="comfortable"
              />
            </v-col>
          </v-row>
          <v-alert type="info" variant="tonal" density="comfortable" class="mt-2" rounded="lg">
            <strong>Shared pool</strong> gives the highest-ranked signal in a tick first claim on the full exposure
            budget -- a lower-ranked signal the same tick only gets whatever's left over, and can be crowded out to 0
            shares. <strong>Equal split</strong> instead gives every trade slot a fixed, equal share of the budget
            (effective budget ÷ Max trades per day), regardless of rank order or timing.
          </v-alert>
        </v-card-text>
      </v-card>

      <v-card flat rounded="lg" class="gap-backtest-card mb-4">
        <v-card-title class="px-5 py-4">
          <span class="text-h6 font-weight-medium">Multi-Stage Exit</span>
        </v-card-title>
        <v-card-text class="px-5 pb-5">
          <v-row>
            <v-col cols="12" md="4">
              <v-select
                v-model="form.exit_strategy"
                :items="[{ title: 'Single-stage (current)', value: 'single_stage' }, { title: 'Multi-stage (pilot)', value: 'multi_stage' }]"
                label="Exit strategy"
                variant="outlined"
                density="comfortable"
                hint="Only open trades use whichever strategy was active when they opened — switching this doesn't affect trades already open"
                persistent-hint
              />
            </v-col>
          </v-row>
          <v-row class="mt-2">
            <v-col cols="6" sm="3">
              <v-text-field
                v-model.number="form.stage1_trigger_pct" type="number" label="Stage 1 trigger (%)"
                variant="outlined" density="comfortable" :disabled="form.exit_strategy !== 'multi_stage'"
                hint="% of the way to target" persistent-hint
              />
            </v-col>
            <v-col cols="6" sm="3">
              <v-text-field
                v-model.number="form.stage1_book_pct" type="number" label="Stage 1 book qty (%)"
                variant="outlined" density="comfortable" :disabled="form.exit_strategy !== 'multi_stage'"
              />
            </v-col>
            <v-col cols="6" sm="3">
              <v-text-field
                v-model.number="form.stage1_lock_pct" type="number" label="Stage 1 stop lock (%)"
                variant="outlined" density="comfortable" :disabled="form.exit_strategy !== 'multi_stage'"
                hint="Stop moves to this % profit level" persistent-hint
              />
            </v-col>
            <v-col cols="6" sm="3">
              <v-text-field
                v-model.number="form.stage2_trigger_pct" type="number" label="Stage 2 trigger (%)"
                variant="outlined" density="comfortable" :disabled="form.exit_strategy !== 'multi_stage'"
              />
            </v-col>
            <v-col cols="6" sm="3">
              <v-text-field
                v-model.number="form.stage2_book_pct" type="number" label="Stage 2 book qty (%)"
                variant="outlined" density="comfortable" :disabled="form.exit_strategy !== 'multi_stage'"
              />
            </v-col>
            <v-col cols="6" sm="3">
              <v-text-field
                v-model.number="form.stage2_lock_pct" type="number" label="Stage 2 stop lock (%)"
                variant="outlined" density="comfortable" :disabled="form.exit_strategy !== 'multi_stage'"
              />
            </v-col>
            <v-col cols="6" sm="3">
              <v-text-field
                v-model.number="form.target_lock_pct" type="number" label="Target stop lock (%)"
                variant="outlined" density="comfortable" :disabled="form.exit_strategy !== 'multi_stage'"
                hint="Stop level once original target is reached" persistent-hint
              />
            </v-col>
            <v-col cols="6" sm="3">
              <v-text-field
                v-model.number="form.runner_extension_pct" type="number" label="Runner extension (%)"
                variant="outlined" density="comfortable" :disabled="form.exit_strategy !== 'multi_stage'"
                hint="Extends target for the final runner leg" persistent-hint
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-card flat rounded="lg" class="gap-backtest-card mb-4">
        <v-card-title class="px-5 py-4 d-flex align-center justify-space-between">
          <span class="text-h6 font-weight-medium">WhatsApp Notifications</span>
          <v-switch v-model="form.whatsapp_notifications_enabled" color="primary" density="compact" hide-details />
        </v-card-title>
        <v-card-text class="px-5 pb-5">
          <div class="text-body-2 text-medium-emphasis">
            Sends a WhatsApp message when a stock is selected, an order is placed, and on every
            profit-target or stop-loss exit. Requires WHATSAPP_ACCESS_TOKEN / WHATSAPP_PHONE_NUMBER_ID /
            WHATSAPP_TO_NUMBER to be configured on the server.
          </div>
        </v-card-text>
      </v-card>

      <v-card flat rounded="lg" class="gap-backtest-card mb-4">
        <v-card-title class="px-5 py-4">
          <span class="text-h6 font-weight-medium">Square-off Time</span>
        </v-card-title>
        <v-card-text class="px-5 pb-5">
          <v-row>
            <v-col cols="6" sm="3" md="2">
              <v-text-field v-model.number="form.square_off_hour" type="number" label="Hour (24h, IST)" variant="outlined" density="comfortable" />
            </v-col>
            <v-col cols="6" sm="3" md="2">
              <v-text-field v-model.number="form.square_off_minute" type="number" label="Minute" variant="outlined" density="comfortable" />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <div class="d-flex justify-end mb-4">
        <v-btn color="primary" variant="flat" rounded="lg" :loading="saving" @click="save">Save Settings</v-btn>
      </div>

      <v-card flat rounded="lg" class="gap-backtest-card mb-4">
        <v-card-title class="px-5 py-4 d-flex align-center justify-space-between">
          <span class="text-h6 font-weight-medium">Settings History</span>
          <v-btn variant="text" size="small" :loading="!versionsLoaded" @click="loadVersions">
            <font-awesome-icon icon="fa-solid fa-rotate-right" class="mr-1" /> Refresh
          </v-btn>
        </v-card-title>
        <v-card-text class="px-5 pb-5">
          <div class="text-body-2 text-medium-emphasis mb-3">
            A new version is recorded every time a save actually changes something. Pick two versions below to see
            what changed and how real trading performed while each was active.
          </div>

          <v-table v-if="versions.length" density="compact" class="mb-4 custom-data-table">
            <thead>
              <tr><th>Version</th><th>Status</th><th>Saved</th><th>Changed</th></tr>
            </thead>
            <tbody>
              <tr v-for="v in reversedVersions" :key="v.version">
                <td>#{{ v.version }}</td>
                <td><v-chip size="x-small" variant="tonal" :color="versionStatusColor(v.status)">{{ v.status || 'active' }}</v-chip></td>
                <td>{{ formatClockIST(v.created_at) }}</td>
                <td class="text-caption">{{ changedFieldsSummary(v.changed_fields) }}</td>
              </tr>
            </tbody>
          </v-table>
          <div v-else class="text-medium-emphasis mb-4">
            No versioned changes yet — the next settings save that actually changes something will create version 1.
          </div>

          <v-row v-if="versions.length >= 2" align="center" class="mb-2">
            <v-col cols="12" sm="4" md="3">
              <v-select v-model="compareFrom" :items="versionOptions" label="From version" variant="outlined" density="comfortable" hide-details />
            </v-col>
            <v-col cols="12" sm="4" md="3">
              <v-select v-model="compareTo" :items="versionOptions" label="To version" variant="outlined" density="comfortable" hide-details />
            </v-col>
            <v-col cols="12" sm="4" md="3">
              <v-btn
                color="primary" variant="tonal" rounded="lg" :loading="comparing"
                :disabled="compareFrom === null || compareTo === null"
                @click="runCompareVersions"
              >
                Compare
              </v-btn>
            </v-col>
          </v-row>

          <template v-if="compareResult">
            <v-divider class="my-4" />
            <v-row>
              <v-col cols="12" md="6">
                <div class="text-subtitle-2 font-weight-medium mb-2">Changed settings</div>
                <v-table v-if="compareResult.diff" density="compact" class="custom-data-table">
                  <thead><tr><th>Field</th><th>From</th><th>To</th></tr></thead>
                  <tbody>
                    <tr v-for="(change, field) in compareResult.diff" :key="field">
                      <td>{{ field }}</td>
                      <td>{{ change.from }}</td>
                      <td>{{ change.to }}</td>
                    </tr>
                  </tbody>
                </v-table>
                <div v-else class="text-medium-emphasis">No settings differ between these two versions.</div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="text-subtitle-2 font-weight-medium mb-2">
                  {{ (compareResult.from.performanceType === 'backtested' || compareResult.to.performanceType === 'backtested') ? 'Performance — live where active, backtested (walk-forward) where testing' : 'Performance while active' }}
                </div>
                <v-table density="compact" class="custom-data-table">
                  <thead>
                    <tr>
                      <th></th>
                      <th>Version #{{ compareResult.from.version }}<template v-if="compareResult.from.performanceType === 'backtested'"> (backtested)</template></th>
                      <th>Version #{{ compareResult.to.version }}<template v-if="compareResult.to.performanceType === 'backtested'"> (backtested)</template></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td class="text-medium-emphasis">Trades (closed)</td>
                      <td>{{ compareResult.from.performance ? compareResult.from.performance.closedCount : '—' }}</td>
                      <td>{{ compareResult.to.performance ? compareResult.to.performance.closedCount : '—' }}</td>
                    </tr>
                    <tr v-if="compareResult.from.performanceType !== 'backtested' || compareResult.to.performanceType !== 'backtested'">
                      <td class="text-medium-emphasis">Win rate</td>
                      <td>{{ winRateCell(compareResult.from) }}</td>
                      <td>{{ winRateCell(compareResult.to) }}</td>
                    </tr>
                    <tr v-if="compareResult.from.performanceType !== 'backtested' || compareResult.to.performanceType !== 'backtested'">
                      <td class="text-medium-emphasis">P&amp;L</td>
                      <td :class="pnlClass(compareResult.from.performance && compareResult.from.performance.totalPnlRs)">
                        {{ compareResult.from.performanceType === 'backtested' ? '—' : formatMoney(compareResult.from.performance && compareResult.from.performance.totalPnlRs) }}
                      </td>
                      <td :class="pnlClass(compareResult.to.performance && compareResult.to.performance.totalPnlRs)">
                        {{ compareResult.to.performanceType === 'backtested' ? '—' : formatMoney(compareResult.to.performance && compareResult.to.performance.totalPnlRs) }}
                      </td>
                    </tr>
                    <tr v-if="compareResult.from.performanceType !== 'backtested' || compareResult.to.performanceType !== 'backtested'">
                      <td class="text-medium-emphasis">Profit factor</td>
                      <td>{{ compareResult.from.performanceType === 'backtested' ? '—' : formatProfitFactor(compareResult.from.performance) }}</td>
                      <td>{{ compareResult.to.performanceType === 'backtested' ? '—' : formatProfitFactor(compareResult.to.performance) }}</td>
                    </tr>
                    <tr>
                      <td class="text-medium-emphasis">Expectancy / trade</td>
                      <td>{{ formatMoney(compareResult.from.performance && compareResult.from.performance.expectancy.expectancyRs) }}</td>
                      <td>{{ formatMoney(compareResult.to.performance && compareResult.to.performance.expectancy.expectancyRs) }}</td>
                    </tr>
                    <tr v-if="compareResult.from.performanceType === 'backtested' || compareResult.to.performanceType === 'backtested'">
                      <td class="text-medium-emphasis">Stability score</td>
                      <td>{{ isFiniteNumber(compareResult.from.performance && compareResult.from.performance.stabilityScore) ? Number(compareResult.from.performance.stabilityScore).toFixed(2) : '—' }}</td>
                      <td>{{ isFiniteNumber(compareResult.to.performance && compareResult.to.performance.stabilityScore) ? Number(compareResult.to.performance.stabilityScore).toFixed(2) : '—' }}</td>
                    </tr>
                  </tbody>
                </v-table>
                <div v-if="compareResult.from.performanceType === 'backtested' || compareResult.to.performanceType === 'backtested'" class="text-caption text-medium-emphasis mt-2">
                  A "testing" version was never live — its figures are from the offline walk-forward backtest that proposed it, not real trading.
                </div>
                <div class="text-caption text-medium-emphasis mt-2">
                  Small sample sizes here are expected early on — treat as directional, not decisive.
                </div>
              </v-col>
            </v-row>
          </template>
        </v-card-text>
      </v-card>

      <v-card flat rounded="lg" class="gap-backtest-card mb-4">
        <v-card-title class="px-5 py-4">
          <span class="text-h6 font-weight-medium">Test Settings on a Specific Day</span>
        </v-card-title>
        <v-card-text class="px-5 pb-5">
          <div class="text-body-2 text-medium-emphasis mb-3">
            Save your settings above first, then pick a past trading day to see how the whole watchlist would have
            been selected and simulated under these settings — not just one stock, the full day's pipeline.
          </div>
          <v-row align="center">
            <v-col cols="12" sm="4" md="3">
              <v-text-field v-model="simulateDate" type="date" label="Date" variant="outlined" density="comfortable" hide-details />
            </v-col>
            <v-col cols="12" sm="4" md="3">
              <v-btn color="primary" variant="tonal" rounded="lg" :loading="simulating" @click="simulateDay">
                Run backtest for this day
              </v-btn>
            </v-col>
          </v-row>

          <v-alert v-if="simulateMessage" :type="simulateError ? 'error' : 'success'" variant="tonal" density="comfortable" class="mt-4" rounded="lg">
            {{ simulateMessage }}
          </v-alert>

          <v-table v-if="simulateResults.length" density="compact" class="mt-4 custom-data-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Direction</th>
                <th>Entry</th>
                <th>Single-stage</th>
                <th>Multi-stage</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in simulateResults" :key="row.code">
                <td>{{ row.code }}</td>
                <td>{{ row.direction }}</td>
                <td>{{ $helper.formatPrice(row.entry_price) }}</td>
                <td :class="Number(row.single_pnl_per_share) >= 0 ? 'text-success' : 'text-error'">
                  {{ row.single_exit_reason }} ({{ Number(row.single_pnl_per_share).toFixed(3) }}/share)
                </td>
                <td :class="Number(row.multi_pnl_per_share) >= 0 ? 'text-success' : 'text-error'">
                  {{ row.multi_exit_reason }} ({{ Number(row.multi_pnl_per_share).toFixed(3) }}/share)
                </td>
              </tr>
            </tbody>
          </v-table>

          <div v-if="simulateRan" class="mt-3">
            <router-link :to="`/gap-backtest/day-review?date=${simulateDate}`">
              View full chart &amp; comparison on Day Review for {{ simulateDate }} →
            </router-link>
          </div>
        </v-card-text>
      </v-card>

      <v-card flat rounded="lg" class="gap-backtest-card">
        <v-card-title class="px-5 py-4">
          <span class="text-h6 font-weight-medium">Watchlist</span>
        </v-card-title>
        <v-card-text class="px-5 pb-5">
          <div class="d-flex flex-wrap ga-2 mb-4">
            <v-chip
              v-for="item in watchlist"
              :key="item.code"
              closable
              variant="tonal"
              color="primary"
              @click:close="removeCode(item.code)"
            >
              {{ item.code }}
            </v-chip>
            <span v-if="!watchlist.length" class="text-medium-emphasis">No stocks in the watchlist yet.</span>
          </div>

          <div class="d-flex ga-2 align-center" style="max-width: 360px">
            <v-text-field
              v-model="newCode"
              label="Stock code (e.g. TCS)"
              variant="outlined"
              density="comfortable"
              hide-details
              @keyup.enter="addCode"
            />
            <v-btn color="primary" variant="tonal" rounded="lg" :loading="adding" @click="addCode">Add</v-btn>
          </div>
        </v-card-text>
      </v-card>
    </div>
  </div>
</template>

<script>
import { fetchWrapper } from '@/helpers/fetch-wrapper';

function todayDateKey() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export default {
  name: 'PaperTradingSettings-page',
  data() {
    return {
      form: {
        order_mode: 'paper',
        total_capital_rs: null,
        risk_per_trade_rs: null,
        max_exposure_rs: null,
        stop_buffer_pct: null,
        stop_suggestion_percentile: null,
        max_slippage_pct: null,
        consecutive_failure_limit: null,
        max_gap_pct: null,
        max_open_price_deviation_pct: null,
        quote_sanity_fail_limit: null,
        daily_loss_limit_enabled: false,
        max_daily_loss_rs: null,
        max_positions_per_sector: null,
        max_sector_exposure_pct: null,
        max_concurrent_positions: null,
        partial_booking_enabled: false,
        partial_trigger_pct: null,
        partial_qty_pct: null,
        partial_sl_action: 'breakeven',
        square_off_hour: null,
        square_off_minute: null,
        max_trades_per_day: null,
        leverage_multiplier: null,
        min_win_rate_threshold_pct: null,
        position_sizing_mode: 'shared_pool',
        exit_strategy: 'single_stage',
        stage1_trigger_pct: null,
        stage1_book_pct: null,
        stage1_lock_pct: null,
        stage2_trigger_pct: null,
        stage2_book_pct: null,
        stage2_lock_pct: null,
        target_lock_pct: null,
        runner_extension_pct: null,
        whatsapp_notifications_enabled: true,
      },
      watchlist: [],
      newCode: '',
      versions: [],
      versionsLoaded: true,
      compareFrom: null,
      compareTo: null,
      comparing: false,
      compareResult: null,
      orderModeSelection: 'paper',
      orderModeConfirmText: '',
      orderModeSaving: false,
      saving: false,
      adding: false,
      saveMessage: '',
      saveError: false,
      simulateDate: todayDateKey(),
      simulating: false,
      simulateRan: false,
      simulateResults: [],
      simulateMessage: '',
      simulateError: false,
      // Risk/exposure crossover warnings from the backend
      // (computeSizingCrossoverWarnings() in
      // paper-trading-settings.controllers.js) -- e.g. "exposure caps real
      // risk to ~Rs 150, risk_per_trade_rs never actually binds". Refreshed
      // on every load and every save, since it's a static function of
      // current settings, not just a save-time check.
      sizingWarnings: [],
    };
  },
  computed: {
    reversedVersions() {
      return [...this.versions].reverse();
    },
    versionOptions() {
      return this.versions.map((v) => ({
        title: `#${v.version}${v.status === 'testing' ? ' (testing)' : ''} — ${this.formatClockIST(v.created_at)}`,
        value: v.version,
      }));
    },
  },
  mounted() {
    this.load();
    this.loadVersions();
  },
  watch: {
    // Picking 'live' never applies by itself -- confirmLiveMode() below is the
    // only path that persists it. Picking 'paper' back is the kill switch, so
    // it applies immediately with no extra confirmation step.
    orderModeSelection(value) {
      if (value === 'paper' && this.form.order_mode !== 'paper') {
        this.setOrderMode('paper');
      }
    },
  },
  methods: {
    load() {
      fetchWrapper.get('api/paper-trading-settings').then((res) => {
        if (res?.data?.settings) {
          Object.keys(this.form).forEach((key) => {
            if (res.data.settings[key] !== undefined && res.data.settings[key] !== null) {
              this.form[key] = res.data.settings[key];
            }
          });
        }
        this.orderModeSelection = this.form.order_mode;
        this.watchlist = res?.data?.watchlist || [];
        this.sizingWarnings = res?.warnings || [];
      }).catch((err) => {
        console.error('Failed to load paper trading settings:', err);
      });
    },
    setOrderMode(mode) {
      this.orderModeSaving = true;
      fetchWrapper.put('api/paper-trading-settings', { order_mode: mode }).then(() => {
        this.form.order_mode = mode;
        this.orderModeSelection = mode;
        this.orderModeConfirmText = '';
        this.saveMessage = mode === 'live' ? 'Live order mode enabled.' : 'Switched back to paper mode.';
        this.saveError = false;
      }).catch((err) => {
        this.orderModeSelection = this.form.order_mode; // revert the select on failure
        this.saveMessage = typeof err === 'string' ? err : 'Failed to change order mode.';
        this.saveError = true;
      }).finally(() => {
        this.orderModeSaving = false;
      });
    },
    confirmLiveMode() {
      if (this.orderModeConfirmText !== 'LIVE') return;
      this.setOrderMode('live');
    },
    save() {
      this.saving = true;
      this.saveMessage = '';
      // Empty means "unlimited" for these three -- a cleared v-model.number
      // field lands as '' rather than null, and '' would coerce to 0 in the
      // DB (a hard "zero allowed" block -- every candidate rejected), the
      // opposite of what clearing the field is meant to do.
      const payload = {
        ...this.form,
        max_positions_per_sector: this.form.max_positions_per_sector === '' ? null : this.form.max_positions_per_sector,
        max_sector_exposure_pct: this.form.max_sector_exposure_pct === '' ? null : this.form.max_sector_exposure_pct,
        max_concurrent_positions: this.form.max_concurrent_positions === '' ? null : this.form.max_concurrent_positions,
      };
      fetchWrapper.put('api/paper-trading-settings', payload).then((res) => {
        this.saveMessage = 'Settings saved.';
        this.saveError = false;
        this.sizingWarnings = res?.warnings || [];
        this.loadVersions(); // may have just created a new version
      }).catch((err) => {
        this.saveMessage = typeof err === 'string' ? err : 'Failed to save settings.';
        this.saveError = true;
      }).finally(() => {
        this.saving = false;
      });
    },
    loadVersions() {
      this.versionsLoaded = false;
      return fetchWrapper.get('api/paper-trading-settings/version-history').then((res) => {
        this.versions = res?.data?.versions || [];
        this.versionsLoaded = true;
        // Default the "From" picker to the currently active version (the
        // highest version whose status isn't 'testing') the first time
        // versions load, so a comparison against "current" needs no manual
        // lookup -- only sets it if the user hasn't already picked one.
        if (this.compareFrom === null) {
          const active = [...this.versions].reverse().find((v) => v.status !== 'testing');
          if (active) this.compareFrom = active.version;
        }
      }).catch((err) => {
        console.error('Failed to load settings version history:', err);
        this.versions = [];
        this.versionsLoaded = true;
      });
    },
    runCompareVersions() {
      if (this.compareFrom === null || this.compareTo === null) return;
      this.comparing = true;
      this.compareResult = null;
      const params = new URLSearchParams({ fromVersion: this.compareFrom, toVersion: this.compareTo });
      fetchWrapper.get(`api/paper-trading-settings/compare-versions?${params.toString()}`).then((res) => {
        this.compareResult = res?.data || null;
      }).catch((err) => {
        console.error('Failed to compare settings versions:', err);
        this.compareResult = null;
      }).finally(() => {
        this.comparing = false;
      });
    },
    changedFieldsSummary(changedFields) {
      if (!changedFields) return '—';
      return Object.entries(changedFields).map(([field, c]) => `${field}: ${c.from} → ${c.to}`).join(', ');
    },
    isFiniteNumber(value) {
      return value !== null && value !== undefined && Number.isFinite(Number(value));
    },
    formatPct(value) {
      return this.isFiniteNumber(value) ? `${Number(value).toFixed(1)}%` : '—';
    },
    formatMoney(value) {
      return this.isFiniteNumber(value) ? this.$helper.formatPrice(Number(value)) : '—';
    },
    pnlClass(value) {
      return this.isFiniteNumber(value) ? (Number(value) >= 0 ? 'text-success' : 'text-error') : '';
    },
    formatProfitFactor(performance) {
      const pf = performance?.profitFactor?.profitFactor;
      return this.isFiniteNumber(pf) ? Number(pf).toFixed(2) : '—';
    },
    versionStatusColor(status) {
      if (status === 'testing') return 'warning';
      if (status === 'archived') return 'default';
      return 'success'; // active (also the default when status is absent, e.g. an older row)
    },
    // A backtested-performance entry has no winRate field at all (see
    // resolveVersionPerformance() in paper-trading-settings.controllers.js)
    // -- guard against that shape before reading .winRate.rawWinRatePct.
    winRateCell(versionEntry) {
      if (versionEntry.performanceType === 'backtested') return '—';
      return this.formatPct(versionEntry.performance?.winRate?.rawWinRatePct);
    },
    formatClockIST(value) {
      if (!value) return '—';
      return new Date(value).toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
      });
    },
    addCode() {
      const code = this.newCode.trim();
      if (!code) return;
      this.adding = true;
      fetchWrapper.post('api/paper-trading-settings/watchlist', { code }).then(() => {
        this.newCode = '';
        this.load();
      }).catch((err) => {
        this.saveMessage = typeof err === 'string' ? err : `Failed to add ${code}.`;
        this.saveError = true;
      }).finally(() => {
        this.adding = false;
      });
    },
    removeCode(code) {
      fetchWrapper.delete(`api/paper-trading-settings/watchlist/${code}`).then(() => {
        this.watchlist = this.watchlist.filter((w) => w.code !== code);
      }).catch((err) => {
        console.error(`Failed to remove ${code}:`, err);
      });
    },
    simulateDay() {
      if (!this.simulateDate) return;
      this.simulating = true;
      this.simulateMessage = '';
      this.simulateResults = [];
      fetchWrapper.post('api/paper-trading-settings/simulate-day', { date: this.simulateDate }).then((res) => {
        this.simulateResults = res?.data?.results || [];
        this.simulateRan = true;
        this.simulateMessage = this.simulateResults.length
          ? `${this.simulateResults.length} trade(s) simulated for ${this.simulateDate} under the current settings.`
          : `No eligible candidates for ${this.simulateDate} under the current settings.`;
        this.simulateError = false;
      }).catch((err) => {
        this.simulateMessage = typeof err === 'string' ? err : `Failed to simulate ${this.simulateDate}.`;
        this.simulateError = true;
      }).finally(() => {
        this.simulating = false;
      });
    },
  },
};
</script>

<style scoped>
.order-mode-live {
  border: 1px solid rgb(var(--v-theme-error)) !important;
}
</style>
